import { HsContact, HsRegistration, HsRegistrationType } from '@hsetu/ngui-views';
import { Contact } from '../models/contact.model';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { inject } from '@angular/core';

export class ContactMapper {
  private readonly _guidGenerator = inject(GuidGeneratorService);

  public mapToHsContact(contact: Contact): HsContact {
    const address =
      contact.addresses && contact.addresses.length > 0
        ? contact.addresses[0]
        : undefined;

    const connect =
      contact.connects && contact.connects.length > 0
        ? contact.connects[0]
        : undefined;

    const mappedContact: HsContact = {
      uId: contact.uId,
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      nameAffix1: contact.nameAffix1 ?? '',
      nameAffix2: contact.nameAffix2 ?? '',
      birthday: contact.birthday 
        ? (contact.birthday instanceof Date 
            ? contact.birthday.toISOString() 
            : new Date(contact.birthday).toISOString()) 
        : undefined,
      companyName: contact.companyName ?? contact.lastName ?? '',
      salutation: contact.salutation ?? '',
      street: address && address.street ? address.street : '',
      houseNumber: address && address.houseNumber ? address.houseNumber : '',
      postalCode: address && address.postalCode ? address.postalCode : '',
      city: address && address.city ? address.city : '',
      emailAddress: connect && connect.emailAddress ? connect.emailAddress : '',
      faxNumber: connect && connect.faxNumber ? connect.faxNumber : '',
      telephoneNumber:
        connect && connect.telephoneNumber ? connect.telephoneNumber : '',
      mobileTelephoneNumber:
        connect && connect.mobileTelephoneNumber
          ? connect.mobileTelephoneNumber
          : '',
      homePage: connect && connect.homePage ? connect.homePage : '',
      title: contact.title ?? '',
      contactType: contact.contactType,
      genderType: contact.genderType,
      district: address && address.district ? address.district : '',
      countryType: address && address.countryType ? address.countryType : 0,
      locationType: address && address.locationType ? address.locationType : 0,
      registrations: contact.registrations
        ? contact.registrations.map((r): HsRegistration => ({
            uId: r.uId,
            contactUId: r.contactUId,
            registrationType: r.registrationType as unknown as HsRegistrationType,
            registrationNumber: r.registrationNumber,
            stampCreate: r.stampCreate,
          }))
        : [],
    };

    return mappedContact;
  }

  public mapToContact(hsContact: HsContact): Contact {
    const contact: Contact = {
      uId: hsContact.uId,
      tenantUId: this._guidGenerator.emptyUUID,
      name: '',
      number: '',
      userUId: this._guidGenerator.emptyUUID,
      concurrencyIdentificator: this._guidGenerator.emptyUUID,
      syncType: 0,
      concurrencyStamp: new Date().toISOString(),
      stampCreate: new Date().toISOString(),
      firstName: hsContact.firstName,
      lastName: hsContact.lastName,
      salutation: hsContact.salutation,
      title: hsContact.title,
      contactType: hsContact.contactType,
      genderType: hsContact.genderType,
      birthday: hsContact.birthday ? new Date(hsContact.birthday) : undefined,
      companyName: hsContact.companyName,
      contactName: '',
      nameAffix1: hsContact.nameAffix1,
      nameAffix2: hsContact.nameAffix2,
      isRead: true,
      addresses: [
        {
          uId: this._guidGenerator.emptyUUID,
          tenantUId: this._guidGenerator.emptyUUID,
          name: '',
          number: '',
          userUId: this._guidGenerator.emptyUUID,
          concurrencyIdentificator: this._guidGenerator.emptyUUID,
          syncType: 0,
          sequenceType: 0,
          concurrencyStamp: new Date().toISOString(),
          stampCreate: new Date().toISOString(),
          addressType: 1,
          countryType: hsContact.countryType,
          postOfficeBox: '',
          contactUId: hsContact.uId,
          street: hsContact.street,
          houseNumber: hsContact.houseNumber,
          postalCode: hsContact.postalCode,
          city: hsContact.city,
          district: hsContact.district,
          locationType: hsContact.locationType,
        },
      ],
      connects: [
        {
          uId: this._guidGenerator.emptyUUID,
          tenantUId: this._guidGenerator.emptyUUID,
          userUId: this._guidGenerator.emptyUUID,
          concurrencyIdentificator: this._guidGenerator.emptyUUID,
          syncType: 0,
          sequenceType: 0,
          concurrencyStamp: new Date().toISOString(),
          stampCreate: new Date().toISOString(),
          contactUId: hsContact.uId,
          emailAddress: hsContact.emailAddress,
          faxNumber: hsContact.faxNumber,
          telephoneNumber: hsContact.telephoneNumber,
          mobileTelephoneNumber: hsContact.mobileTelephoneNumber,
          homePage: hsContact.homePage,
        },
      ],
      bankAccounts: [
        {
          uId: this._guidGenerator.emptyUUID,
          tenantUId: this._guidGenerator.emptyUUID,
          userUId: this._guidGenerator.emptyUUID,
          concurrencyIdentificator: this._guidGenerator.emptyUUID,
          syncType: 0,
          sequenceType: 0,
          concurrencyStamp: new Date().toISOString(),
          stampCreate: new Date().toISOString(),
          contactUId: hsContact.uId,
        },
      ],
      registrations: [],
    };

    return contact;
  }

  public mapHsContactToContact(hsContact: HsContact, contact: Contact) {
    contact.firstName = hsContact.firstName;
    contact.lastName = hsContact.lastName;
    contact.salutation = hsContact.salutation;
    contact.title = hsContact.title;
    contact.contactType = hsContact.contactType;
    contact.genderType = hsContact.genderType;
    contact.birthday = hsContact.birthday
      ? new Date(hsContact.birthday)
      : undefined;
    contact.companyName = hsContact.companyName;
    contact.nameAffix1 = hsContact.nameAffix1;
    contact.nameAffix2 = hsContact.nameAffix2;

    if (contact.addresses && contact.addresses.length > 0) {
      const address = contact.addresses[0];
      address.street = hsContact.street;
      address.countryType = hsContact.countryType;
      address.contactUId = contact.uId;
      address.street = hsContact.street;
      address.houseNumber = hsContact.houseNumber;
      address.postalCode = hsContact.postalCode;
      address.city = hsContact.city;
      address.district = hsContact.district;
      address.locationType = hsContact.locationType;
    }

    if (contact.connects && contact.connects.length > 0) {
      const connect = contact.connects[0];
      connect.emailAddress = hsContact.emailAddress;
      connect.faxNumber = hsContact.faxNumber;
      connect.telephoneNumber = hsContact.telephoneNumber;
      connect.mobileTelephoneNumber = hsContact.mobileTelephoneNumber;
      connect.homePage = hsContact.homePage;
      connect.contactUId = contact.uId;
    }
  }
}
